<template>
  <AwSelectMenu
    v-bind="props"
    multiple
    :placeholder="props.title"
    :items="teamMemberOptions"
    value-key="id"
    content-class="min-w-fit"
    :is-loading="loading"
    :max-visible-items="maxVisibleItems"
    :search-input="!loading"
    :model-value="loading ? undefined : props.teamMemberIds"
    @update:model-value="(val) => emit('update:teamMemberIds', val)"
  >
    <template #default="{ modelValue }">
      <SelectMenuBadgeList
        v-slot="{ item }"
        :title="props.title"
        icon="i-ph-user-circle"
        :loading="loading"
        :list="modelValue"
        :max-visible-items="maxVisibleItems"
        :is-removable="isRemovable && !disabled"
        @update:list="
          if ($event && !props.disabled) {
            emit('update:teamMemberIds', $event);
          }
        "
      >
        <div
          v-if="teamMemberOptions.find((tm) => tm.id === item)?.auth_user"
          :style="`${teamMemberOptions.find((tm) => tm.id === item)?.offboarded_at ? 'text-decoration: line-through' : ''}`"
        >
          {{ getFullName(teamMemberOptions.find((tm) => tm.id === item)!) }}
        </div>
      </SelectMenuBadgeList>
    </template>
    <template #item-label="{ item }">
      <div class="team-member-container" :style="`${item.offboarded_at ? 'text-decoration: line-through' : ''}`">
        <LazyTeamMemberAvatar :team-member="item" />
        {{ getFullName(item) }}
        <LazyAwBadge :color="item.is_independent ? 'gray' : 'blue'" size="sm" :is-rounded="true">
          {{ item.is_independent ? "Indépendant" : "Salarié" }}
        </LazyAwBadge>
        <template v-if="props.showRoleBadges">
          <LazyAppRoleTag
            v-for="userRole in item.auth_user?.user_role"
            :key="userRole.role"
            :app-role="userRole.role"
          />
          <LazyAwBadge color="gray" size="sm" :is-rounded="true"> Niveau {{ item.management_level }} </LazyAwBadge>
        </template>
      </div>
    </template>
  </AwSelectMenu>
</template>

<script setup lang="ts">
import { useSupabase } from "~/composables/useSupabase";
import { getFullName } from "@asap/shared/utils/people";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import type { TeamMemberForAvatar } from "@asap/shared/utils/supabase.types";
import { userRoleWithTeamMemberColumns } from "@asap/shared/utils/supabase.columns";

type TeamMemberOption = TeamMemberForAvatar & {
  label: string;
  is_independent: boolean | null;
};

type Props = {
  title?: string;
  roles?: AppRole[];
  teamMemberIds?: string[];
  isRemovable?: boolean;
  maxVisibleItems?: number;
  showRoleBadges?: boolean;
  disabled?: boolean;
};

type Emits = {
  "update:teamMemberIds": [teamMemberIds: string[]];
};

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  teamMemberIds: undefined,
  roles: undefined,
  title: "Consultants",
  isRemovable: true,
  maxVisibleItems: 1,
  showRoleBadges: false,
  disabled: false,
});

const supabase = useSupabase();

const teamMemberOptions = ref<TeamMemberOption[]>([]);
const loading = ref(false);

onMounted(async () => {
  loading.value = true;

  const query = supabase.from("user_role").select(userRoleWithTeamMemberColumns);

  if (props.roles) {
    query.in("role", props.roles);
  }

  const { data, error } = await query;

  if (error) throw error;

  teamMemberOptions.value = data.map((tm) => ({
    ...tm.auth_user.team_member,
    label: tm?.auth_user.first_name + " " + tm?.auth_user.last_name,
  }));

  loading.value = false;
});
</script>

<style scoped>
.team-member-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
</style>
